import React from 'react';
import styled from 'styled-components';
import ThemeToggle from './ThemeToggle';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 5%;
  background: ${props => props.scrolled ? props.theme.colors.headerSolid : props.theme.colors.headerTransparent};
  backdrop-filter: ${props => props.scrolled ? 'blur(10px)' : 'none'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.scrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none'};
  
  @media (max-width: 768px) {
    padding: 1rem 5%;
  }
`;

const Logo = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => {
    // Si scrolled OU (non scrolled ET thème clair), alors texte noir
    if (props.scrolled || (!props.scrolled && props.theme.name === 'light')) {
      return props.theme.colors.navText;
    }
    // Sinon, texte blanc
    return 'white';
  }};
  transition: color 0.3s ease;
  
  span {
    color: ${props => props.theme.colors.accent};
  }
`;

const Nav = styled.nav`
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const NavItem = styled.li`
  margin: 0 1.5rem;
  
  a {
    font-size: 1rem;
    font-weight: 500;
    color: ${props => {
    // Si scrolled OU (non scrolled ET thème clair), alors texte noir
    if (props.scrolled || (!props.scrolled && props.theme.name === 'light')) {
      return props.theme.colors.navText;
    }
    // Sinon, texte blanc
    return 'white';
  }};
    transition: color 0.3s ease;
    text-decoration: none;
    
    &:hover {
      color: ${props => props.theme.colors.accent};
    }
  }
`;

const HeaderRightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ContactBtn = styled.button`
  background: ${props => props.theme.colors.accentGradient};
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(94, 23, 235, 0.3);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuBtn = styled.button`
  display: none;
  cursor: pointer;
  background: transparent;
  border: none;
  width: 30px;
  height: 30px;
  position: relative;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

// Hamburger classique animé
const HamburgerIcon = styled.div`
  position: relative;
  width: 30px;
  height: 20px;

  span {
    display: block;
    position: absolute;
    height: 3px;
    width: 100%;
    background: ${props => {
    if (props.scrolled || (!props.scrolled && props.theme.name === 'light')) {
      return props.theme.colors.hamburgerColor;
    }
    return 'white';
  }};
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: .25s ease-in-out;
    
    &:nth-child(1) {
      top: ${props => props.isOpen ? '9px' : '0px'};
      transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }
    
    &:nth-child(2) {
      top: 9px;
      opacity: ${props => props.isOpen ? '0' : '1'};
    }
    
    &:nth-child(3) {
      top: ${props => props.isOpen ? '9px' : '18px'};
      transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

// Menu mobile pour le responsive
const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.colors.headerSolid};
  z-index: 999;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const MobileNavItems = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: center;
`;

const MobileNavItem = styled.li`
  margin: 1.5rem 0;
  
  a {
    font-size: 1.5rem;
    font-weight: 500;
    color: ${props => props.theme.colors.navText};
    text-decoration: none;
    
    &:hover {
      color: ${props => props.theme.colors.accent};
    }
  }
`;

const Header = ({ isScrolled, menuOpen, setMenuOpen, isStandalonePage = false }) => {
  return (
    <>
      <HeaderContainer scrolled={isScrolled}>
        <Logo scrolled={isScrolled}>
          {isStandalonePage ? (
            <a href="/">Rank<span>eed</span></a>
          ) : (
            <>Rank<span>eed</span></>
          )}
        </Logo>

        <Nav>
          <NavList>
            <NavItem scrolled={isScrolled}>
              {isStandalonePage ? (
                <a href="/#accueil">Accueil</a>
              ) : (
                <a href="#accueil">Accueil</a>
              )}
            </NavItem>
            <NavItem scrolled={isScrolled}>
              {isStandalonePage ? (
                <a href="/#services">Services</a>
              ) : (
                <a href="#services">Services</a>
              )}
            </NavItem>
            <NavItem scrolled={isScrolled}>
              {isStandalonePage ? (
                <a href="/#portfolio">Portfolio</a>
              ) : (
                <a href="#portfolio">Portfolio</a>
              )}
            </NavItem>
          </NavList>
        </Nav>

        <HeaderRightSection>
          <ThemeToggle />
          <ContactBtn>
            {isStandalonePage ? (
              <a href='/#contact'>Nous contacter</a>
            ) : (
              <a href='#contact'>Nous contacter</a>
            )}
          </ContactBtn>
          <MobileMenuBtn
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={menuOpen}
          >
            <HamburgerIcon scrolled={isScrolled} isOpen={menuOpen}>
              <span></span>
              <span></span>
              <span></span>
            </HamburgerIcon>
          </MobileMenuBtn>
        </HeaderRightSection>
      </HeaderContainer>

      {/* Menu mobile pour le responsive */}
      <MobileMenu isOpen={menuOpen}>
        <MobileNavItems>
          <MobileNavItem>
            {isStandalonePage ? (
              <a href="/#accueil" onClick={() => setMenuOpen(false)}>Accueil</a>
            ) : (
              <a href="#accueil" onClick={() => setMenuOpen(false)}>Accueil</a>
            )}
          </MobileNavItem>
          <MobileNavItem>
            {isStandalonePage ? (
              <a href="/#services" onClick={() => setMenuOpen(false)}>Services</a>
            ) : (
              <a href="#services" onClick={() => setMenuOpen(false)}>Services</a>
            )}
          </MobileNavItem>
          <MobileNavItem>
            {isStandalonePage ? (
              <a href="/#portfolio" onClick={() => setMenuOpen(false)}>Portfolio</a>
            ) : (
              <a href="#portfolio" onClick={() => setMenuOpen(false)}>Portfolio</a>
            )}
          </MobileNavItem>
          <MobileNavItem>
            {isStandalonePage ? (
              <a href="/#contact" onClick={() => setMenuOpen(false)}>Contact</a>
            ) : (
              <a href="#contact" onClick={() => setMenuOpen(false)}>Contact</a>
            )}
          </MobileNavItem>
        </MobileNavItems>
      </MobileMenu>
    </>
  );
};

export default Header;